// Query and data functions related to the logged-in homepage (LIHP)
import {t} from "@lingui/core/macro";
import gql from "graphql-tag";
import moment from "moment";
// @ts-expect-error - TS2724 - '"react-router-dom"' has no exported member named 'Match'. Did you mean 'match'? | TS2305 - Module '"react-router-dom"' has no exported member 'Location'.
import type {Location, Match} from "react-router-dom";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import type {AsyncApolloQueryResult} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import type {LearnerKhanmigoEligibility} from "#dashboard-promos-package/use-get-learner-dashboard-promo-eligibility.ts";

import type {
    homepageQueryV4_user_homepageModules_navigation_classes as GqlClassDetails,
    homepageQueryV4_user_homepageModules_navigation_classes_classroom as GqlClassroom,
    homepageQueryV4_user_userDistrictInfos as GqlUserDistrictInfo,
    homepageQueryV4,
    homepageQueryV4Variables,
} from "./__graphql-types__/homepageQueryV4.ts";
import {byClassActiveness} from "./side-nav/sorting.ts";

type ShowWelcomeStateFunc = (
    arg1?: string | null | undefined,
    arg2?: string | null | undefined,
) => boolean;
type CurriedGetWelcomeStates = (
    arg1?: string | null | undefined,
    arg2?: string | null | undefined,
) => WelcomeStates;
type SyncProp = "isKmapSynced" | "isDistrictSynced";
// TODO (jsatk): Make `types.js` file for `logged-in-homepage`.
export type WelcomeStates = "kmap" | "k4d" | undefined;

export const HOMEPAGE_QUERY: GqlOperation<
    homepageQueryV4,
    homepageQueryV4Variables
> = gqlOp(gql`
    query homepageQueryV4($kaid: String, $username: String) {
        actorUser: user {
            id
            hasAccessToAIGuideLearner
            isAIGuideEnabled
            canViewAiGuideHistory: hasPermission(
                name: "can_view_ai_guide_history"
            )
            canManageDistrictStudent(studentKaid: $kaid)
        }
        user(kaid: $kaid, username: $username) {
            id
            kaid
            isActor
            isCoachedByActor
            isManagedByActor
            isAIGuideEnabled
            canViewUserProgress: actorHasUserScopedPermission(
                capability: CAN_VIEW_USER_PROGRESS
            )
            canViewTeachersAndClassrooms: actorHasUserScopedPermission(
                capability: CAN_VIEW_TEACHERS_AND_CLASSROOMS
            )

            isDistrictSynced
            isKmapSynced

            userDistrictInfos {
                id
                currentMathAssessment {
                    id
                    mapTestKey
                }
                gradeLevel
            }

            homepageModules {
                navigation {
                    myCoursesEnabled
                    interestedInLsat

                    classes {
                        classroom {
                            id
                            descriptor
                            cacheId
                            name
                            coach {
                                id
                                kaid
                            }
                            signupCode
                            topics {
                                id
                            }
                            createdOn
                        }
                        showAssignments
                        hasActiveAssignments
                        hasKmapGoals
                        hasNonMapGoals
                        isKmap
                        isK4d
                        latestDueDate
                    }
                }
            }
        }
        hasAccessToAIGuideDev
    }
`);

export type HomepageQuery = AsyncApolloQueryResult<
    homepageQueryV4,
    homepageQueryV4Variables
>;

export type SideNavClass = {
    className: string;
    classId: string;
    classroomDescriptor: string;
    coachKaid: string;
    hasAssignments: boolean;
    hasGoals: boolean;
    hasMapGoals: boolean;
    signupCode: string;
    isK4d: boolean;
    isKmap: boolean;
    hasActiveAssignments: boolean;
    courseIds: Array<string>;
    latestDueDate: moment.Moment | null;
    createdOn: moment.Moment | null;
};

type LinkMatcher = (match: Match, location: Location) => boolean;
export type NavLinkDetail = {
    label: string;
    href: string;
    linkMatcher?: LinkMatcher;
    testId?: string;
};
export type ClassLink = NavLinkDetail & {
    isActive: boolean;
    signupCode: string;
    linkMatcher: LinkMatcher;
};

export type MyClassesSection = {
    label: string;
    links: Array<ClassLink>;
};

export type NavSectionDetail = {
    label: string;
    links: Array<NavLinkDetail>;
};

export type KhanmigoPromoSectionDetail = {
    label: string;
};

type NavDetails = {
    classSection: MyClassesSection | null;
    userSections: Array<NavSectionDetail>;
    khanmigoPromoSection: KhanmigoPromoSectionDetail | null;
    defaultRoute: string;
};

export const getSideNavClasses = (query: HomepageQuery): Array<SideNavClass> =>
    getClasses(query)
        .map((c) => ({
            className: c.classroom?.name || "",
            classId: c.classroom?.id || "",
            classroomDescriptor: c.classroom?.descriptor || "",
            coachKaid: c.classroom?.coach?.kaid || "",
            hasAssignments: !!c.showAssignments,
            hasActiveAssignments: !!c.hasActiveAssignments,
            hasGoals: c.hasNonMapGoals || !!(c.isK4d && !c.isKmap),
            hasMapGoals: c.hasKmapGoals || !!c.isKmap,
            signupCode: c.classroom?.signupCode || "",
            isK4d: !!c.isK4d,
            isKmap: !!c.isKmap,
            courseIds:
                c.classroom?.topics
                    ?.map((topic) => topic?.id)
                    .filter(isTruthy) ?? [],
            latestDueDate: c.latestDueDate ? moment(c.latestDueDate) : null,
            createdOn: c.classroom?.createdOn
                ? moment(c.classroom.createdOn)
                : null,
        }))
        .filter(
            // TODO (CLASS-4501): remove this once ticket is fixed
            (c) => c.coachKaid,
        );

export const isClassActive = (
    sideNavClass: SideNavClass,
    time: moment.Moment,
): boolean => {
    const activeClassCutoffTime = time.clone().subtract(1, "years");

    return (
        !!sideNavClass.createdOn?.isAfter(activeClassCutoffTime) ||
        !!sideNavClass.latestDueDate?.isAfter(activeClassCutoffTime)
    );
};

const getClasses = (query: HomepageQuery): Array<GqlClassDetails> => {
    const classes = query?.data?.user?.homepageModules?.navigation?.classes;

    if (!classes) {
        return [];
    }

    return classes.filter(isTruthy).filter(
        // All classes shown must have coach information
        // TODO (CLASS-4501): remove this once ticket is fixed
        (c) => c.classroom?.coach?.kaid,
    );
};

const isSynced = (query: HomepageQuery, syncProp: SyncProp): boolean =>
    (query.data && query.data.user && query.data.user[syncProp]) || false;

// `classes` should only ever be an array of length 0 or 1.
const getClassDetails = (
    classId: string | null | undefined,
    coachKaid: string | null | undefined,
    syncProp: SyncProp,
    query: HomepageQuery,
): GqlClassDetails | undefined => {
    const classes = getClasses(query);

    if (classes.length === 0 || !isSynced(query, syncProp)) {
        return;
    }

    return classes.find(
        ({classroom}) =>
            classroom?.id === classId &&
            classroom?.coach &&
            classroom.coach.kaid === coachKaid,
    );
};

const classHasKmapGoals =
    (query: HomepageQuery): ShowWelcomeStateFunc =>
    (classId, coachKaid): boolean => {
        const classDetails = getClassDetails(
            classId,
            coachKaid,
            "isKmapSynced",
            query,
        );

        // NOTE (jsatk): If we do not have any classes that match the passed-in
        // Class ID & Coach ID or if the Class is not a MAP class or if the
        // student already has MAP goals in the class then do not show the
        // MAP Welcome Banner.
        const alreadyHasKmapGoals =
            !classDetails || !classDetails.isKmap || classDetails.hasKmapGoals;

        return !alreadyHasKmapGoals;
    };

const classHasCourseGoals =
    (query: HomepageQuery): ShowWelcomeStateFunc =>
    (classId, coachKaid): boolean => {
        const classDetails = getClassDetails(
            classId,
            coachKaid,
            "isDistrictSynced",
            query,
        );

        // NOTE (jsatk): If we do not have any classes that match the passed-in
        // Class ID & Coach ID or if the Class is not K4D or if the Class is a
        // MAP class or if the student already has Course goals in the class
        // then do not show the K4D Welcome Banner.
        const alreadyHasCourseGoals =
            !classDetails ||
            !classDetails.isK4d ||
            classDetails.isKmap ||
            classDetails.hasNonMapGoals;

        return !alreadyHasCourseGoals;
    };

export const getWelcomeState = (
    query: HomepageQuery,
): CurriedGetWelcomeStates => {
    const shouldShowKmapWelcomeState = classHasKmapGoals(query);
    const shouldShowK4DWelcomeState = classHasCourseGoals(query);

    return (classId, coachKaid): WelcomeStates => {
        if (shouldShowKmapWelcomeState(classId, coachKaid)) {
            return "kmap";
        }
        if (shouldShowK4DWelcomeState(classId, coachKaid)) {
            return "k4d";
        }
    };
};

function hasClassPicker(query: HomepageQuery): boolean {
    return Boolean(
        query.data &&
            query.data.user &&
            query.data.user.homepageModules &&
            query.data.user.homepageModules.navigation &&
            query.data.user.homepageModules.navigation.myCoursesEnabled,
    );
}

export function isActivityAccessible(query: HomepageQuery): boolean {
    if (!query.data || !query.data.user) {
        return false;
    }
    return Boolean(
        query.data.user.isActor ||
            query.data.user.isCoachedByActor ||
            query.data.user.canViewUserProgress,
    );
}

export function areGuideActivitiesAccessible(query: HomepageQuery): boolean {
    if (!query.data || !query.data.user) {
        return false;
    }
    return Boolean(
        query.data.user.isActor &&
            query.data.actorUser?.isAIGuideEnabled &&
            query.data.actorUser?.hasAccessToAIGuideLearner,
    );
}

export function isGuideHistoryAccessible(query: HomepageQuery): boolean {
    if (!query.data || !query.data.user) {
        return false;
    }
    return Boolean(
        (query.data.user.isActor ||
            query.data.user.isCoachedByActor ||
            query.data.actorUser?.canManageDistrictStudent ||
            query.data.actorUser?.canViewAiGuideHistory) &&
            (query.data.user.isAIGuideEnabled ||
                query.data.actorUser?.isAIGuideEnabled),
    );
}

export function isGuideSettingsAccessible(query: HomepageQuery): boolean {
    if (!query.data || !query.data.user) {
        return false;
    }
    return Boolean(
        (query.data.user.isActor || query.data.user.isCoachedByActor) &&
            query.data.user.isAIGuideEnabled,
    );
}

export function isKhanmigoPromoAccessible(
    eligibilityResult: LearnerKhanmigoEligibility,
): boolean {
    if (eligibilityResult.status !== "success") {
        return false;
    }

    return !!eligibilityResult.isEligible;
}

export function isCoachListReadable(query: HomepageQuery): boolean {
    if (!query.data || !query.data.user) {
        return false;
    }
    return Boolean(
        query.data.user.isActor ||
            query.data.user.isManagedByActor ||
            query.data.user.canViewTeachersAndClassrooms,
    );
}

export function getIsKmapSynced(query: HomepageQuery): boolean {
    return Boolean(
        query.data && query.data.user && query.data.user.isKmapSynced,
    );
}

export function getUserDistrictInfo(
    query: HomepageQuery,
): GqlUserDistrictInfo | null | undefined {
    if (query.data && query.data.user && query.data.user.userDistrictInfos) {
        return query.data.user.userDistrictInfos[0];
    }
}

function isInterestedInLsat(query: HomepageQuery): boolean {
    return Boolean(
        query.data &&
            query.data.user &&
            query.data.user.homepageModules &&
            query.data.user.homepageModules.navigation &&
            query.data.user.homepageModules.navigation.interestedInLsat,
    );
}

export function getRelationship(
    query: HomepageQuery,
): "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER" {
    const user = query?.data?.user;
    if (user?.isCoachedByActor) {
        return "COACH";
    }

    if (user?.isActor) {
        return "SELF";
    }

    if (query?.data?.actorUser?.canManageDistrictStudent) {
        return "DISTRICT_ADMIN";
    }

    return "OTHER";
}

const isCurrentProfilePage = (profileRoot: string): LinkMatcher => {
    if (profileRoot.endsWith("/")) {
        // Remove the trailing slash so we can compare paths
        // with and without the trailing slash.
        profileRoot = profileRoot.substr(0, profileRoot.length - 1);
    }

    const profilePages = [
        "",
        "/",
        "/badges",
        "/discussion",
        "/discussion/questions",
        "/discussion/projectquestions",
        "/discussion/answers",
        "/discussion/replies",
        "/discussion/comments",
        "/projects",
    ].map((x) => `${profileRoot}${x}`);

    return (match: unknown, location: unknown) =>
        Boolean(
            location &&
                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                location.pathname &&
                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                profilePages.includes(location.pathname),
        );
};

const isCurrentPage =
    (root: string): LinkMatcher =>
    (match, location): boolean =>
        Boolean(
            location && location.pathname && location.pathname.startsWith(root),
        );

// Deprecated: Use classDescriptorClassLink instead
export const classLink = ({
    profileRoot,
    classId,
    coachKaid,
}: {
    profileRoot: string;
    classId: string;
    coachKaid: string;
}) => {
    return `${profileRoot}class/${coachKaid}/${classId}`;
};

export const classDescriptorClassLink = ({
    profileRoot,
    classroomDescriptor,
}: {
    profileRoot: string;
    classroomDescriptor: string;
}) => {
    const encodedClassroomDescriptor = encodeURIComponent(classroomDescriptor);
    return `${profileRoot}classroom/${encodedClassroomDescriptor}`;
};

export function getSideNavLinks(
    query: HomepageQuery,
    profileRoot: string,
    time: moment.Moment,
    {
        isActivityAccessible,
        isMyPage,
        isCoachListReadable,
        areGuideActivitiesAccessible,
        isGuideHistoryAccessible,
        isGuideSettingsAccessible,
        isKhanmigoPromoAccessible,
    }: {
        isActivityAccessible: boolean;
        isMyPage: boolean;
        isCoachListReadable: boolean;
        areGuideActivitiesAccessible: boolean;
        isGuideHistoryAccessible: boolean;
        isGuideSettingsAccessible: boolean;
        isKhanmigoPromoAccessible: boolean;
    },
): NavDetails {
    let classSection: MyClassesSection | null = null;
    const userSections: Array<NavSectionDetail> = [];
    let khanmigoPromoSection: KhanmigoPromoSectionDetail | null = null;

    // Gather up the classes
    if (isMyPage) {
        const classes = getSideNavClasses(query)
            .filter(
                (sideNavClass) =>
                    sideNavClass.hasAssignments ||
                    sideNavClass.hasGoals ||
                    sideNavClass.hasMapGoals ||
                    isClassActive(sideNavClass, time),
            )
            .sort(byClassActiveness);
        if (classes.length > 0) {
            classSection = {
                label: t`My classes`,
                links: classes.map((cd) => {
                    const href = classDescriptorClassLink({
                        profileRoot,
                        classroomDescriptor: cd.classroomDescriptor,
                    });
                    return {
                        label: cd.className,
                        href,
                        linkMatcher: (_, {pathname}) => pathname === href,
                        isActive: isClassActive(cd, time),
                        signupCode: cd.signupCode,
                    };
                }),
            };
        }

        // Gather up the independent learning section
        const ilLinks: Array<NavLinkDetail> = [];
        if (hasClassPicker(query)) {
            const href = `${profileRoot}courses`;
            ilLinks.push({
                label: t`Courses`,
                href,
                testId: "side-nav-courses",
                linkMatcher: (_, location) => location.pathname === href,
            });
        }

        if (ilLinks.length > 0) {
            userSections.push({
                label: t`My stuff`,
                links: ilLinks,
            });
        }
    }

    // Gather up the Account section
    const progressRoot = `${profileRoot}progress`;
    const isCurrentProgressPage = isCurrentPage(progressRoot);
    const maybeProgressLink = isActivityAccessible
        ? [
              {
                  label: t`Progress`,
                  href: progressRoot,
                  linkMatcher: isCurrentProgressPage,
                  testId: "side-nav-progress",
              },
          ]
        : [];

    const guideHistoryRoot = `${profileRoot}khanmigo/history`;
    const isCurrentGuideHistoryPage = isCurrentPage(guideHistoryRoot);
    const guideHistoryLink = isGuideHistoryAccessible
        ? [
              {
                  label: t`Chat History`,
                  href: guideHistoryRoot,
                  linkMatcher: isCurrentGuideHistoryPage,
                  testId: "side-nav-guide-history",
              },
          ]
        : [];

    const guideSettingsRoot = `${profileRoot}khanmigo/settings`;
    const isCurrentGuideSettingsPage = isCurrentPage(guideSettingsRoot);
    const guideSettingsLink = isGuideSettingsAccessible
        ? [
              {
                  label: t`Khanmigo Settings`,
                  href: guideSettingsRoot,
                  linkMatcher: isCurrentGuideSettingsPage,
                  testId: "side-nav-guide-settings",
              },
          ]
        : [];

    // TODO (GL-127): Create the Activities Page
    const guideActivitiesRoot = `${profileRoot}khanmigo/activities`;
    const isCurrentGuidePage = isCurrentPage(guideActivitiesRoot);
    const guideActivitiesLink = areGuideActivitiesAccessible
        ? [
              {
                  label: t`Activities`,
                  href: guideActivitiesRoot,
                  linkMatcher: isCurrentGuidePage,
                  testId: "side-nav-guide-activities",
              },
          ]
        : [];

    const teacherRoot = `${profileRoot}teachers`;
    const isCurrentTeacherPage = isCurrentPage(teacherRoot);
    const maybeTeachersLink = isCoachListReadable
        ? [
              {
                  label: t`Teachers`,
                  href: teacherRoot,
                  linkMatcher: isCurrentTeacherPage,
                  testId: "side-nav-teachers",
              },
          ]
        : [];

    const profileLink = [
        {
            label: t`Profile`,
            href: `${profileRoot}`,
            linkMatcher: isCurrentProfilePage(profileRoot),
            testId: "side-nav-profile",
        },
    ];

    const accountSection = {
        label: isMyPage ? t`My account` : t`Account`,
        // NOTE (jsatk): The order here 👇 in `links` dictates the order these
        // links will appear in the right-hand navigation.  If you reorder these
        // please make sure design/product is consulted and tests are updated.
        links: [...maybeProgressLink, ...profileLink, ...maybeTeachersLink],
    };

    userSections.push(accountSection);

    if (
        areGuideActivitiesAccessible ||
        isGuideHistoryAccessible ||
        isGuideSettingsAccessible
    ) {
        const guideSection = {
            label: t`Khanmigo`,
            links: [
                ...guideActivitiesLink,
                ...guideHistoryLink,
                ...guideSettingsLink,
            ],
        };
        userSections.push(guideSection);
    } else if (isKhanmigoPromoAccessible) {
        khanmigoPromoSection = {
            label: t`Khanmigo`,
        };
    }

    const defaultSection = classSection || userSections[0];

    return {
        classSection,
        userSections,
        khanmigoPromoSection,
        defaultRoute: defaultSection.links[0].href,
    };
}

export function userNotFound(query: HomepageQuery): boolean {
    // If we're done loading and we have a data object but the
    // user is empty, then we didn't find the user.
    return !query.loading && !!query.data && !query.data.user;
}

function findClass(
    classId: string | null | undefined,
    coachKaid: string | null | undefined,
    query: HomepageQuery,
): GqlClassDetails | null | undefined {
    if (query.loading || !query.data) {
        return;
    }

    const user = query.data.user;
    if (!user || !user.homepageModules || !user.homepageModules.navigation) {
        return;
    }

    const classes = getClasses(query);

    const getCoachKaid = (classroom?: GqlClassroom | null) =>
        classroom?.coach ? classroom.coach.kaid : "";

    return classes.find(
        (c) =>
            c.classroom?.id === classId &&
            getCoachKaid(c?.classroom) === coachKaid,
    );
}

export function getClassHasActiveAssignment(
    classId: string | null | undefined,
    coachKaid: string | null | undefined,
    query: HomepageQuery,
): boolean {
    const foundClass = findClass(classId, coachKaid, query);

    if (!foundClass) {
        return false;
    }

    return !!foundClass.hasActiveAssignments;
}

export function getClassNameForClassId(
    classId: string | null | undefined,
    coachKaid: string | null | undefined,
    query: HomepageQuery,
): string {
    const foundClass = findClass(classId, coachKaid, query);

    if (!foundClass) {
        return "";
    }

    return foundClass.classroom?.name || "";
}

export function getClassDescriptor(
    classId: string | null | undefined,
    coachKaid: string | null | undefined,
    query: HomepageQuery,
): string {
    const foundClass = findClass(classId, coachKaid, query);

    if (!foundClass) {
        return "";
    }

    return foundClass.classroom?.descriptor || "";
}

export const internal = {
    classHasCourseGoals,
    classHasKmapGoals,
    getClassNameForClassId,
    getSideNavClasses,
    hasClassPicker,
    isCurrentPage,
    isCurrentProfilePage,
    isInterestedInLsat,
} as const;
